import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { fetchInternalScrapingJobs } from '../remote/fetch-internal-scraping-jobs';

import type { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import { queryClient } from '@/shared/query-client';

type ScrapingJobType = 'GOOGLE_SEARCH' | 'DIRECT_URL';

function generateQueryKey(
  type: ScrapingJobType,
  page: number,
  limit: number,
  platform?: SOURCE_PLATFORM_TYPE,
  keyword?: string,
  jobType?: 'all' | 'onetime' | 'periodic',
  status?: 'all' | 'IN_PROGRESS' | 'DONE',
  owner?: string,
) {
  const prefix = type === 'GOOGLE_SEARCH' ? 'scraping-jobs' : 'url-scraping-jobs';
  return [prefix, page, limit, platform, keyword, jobType, status, owner].filter(Boolean);
}

function generateQueryKeyPrefix(type: ScrapingJobType) {
  return type === 'GOOGLE_SEARCH' ? ['scraping-jobs'] : ['url-scraping-jobs'];
}

export function useScrapingJobs<T extends ScrapingJobType>({
  type,
  limit = 10,
  platform,
  keyword,
  jobType,
  status,
  owner,
}: {
  type: T;
  limit: number;
  platform?: SOURCE_PLATFORM_TYPE;
  keyword?: string;
  jobType?: 'all' | 'onetime' | 'periodic';
  status?: 'all' | 'IN_PROGRESS' | 'DONE';
  owner?: string;
}) {
  const [currentPage, setCurrentPage] = useState(1);

  const query = useQuery({
    queryKey: generateQueryKey(type, currentPage, limit, platform, keyword, jobType, status, owner),
    queryFn: () =>
      fetchInternalScrapingJobs({
        page: currentPage,
        limit,
        platform,
        type,
        keyword: keyword && keyword.trim() !== '' ? keyword : undefined,
        jobType: jobType === 'all' ? undefined : jobType,
        status: status === 'all' ? undefined : status,
        owner: owner && owner.trim() !== '' ? owner : undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < (query.data?.totalPages ?? 1);

  const goToPrevious = () => {
    if (canGoPrevious) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (canGoNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= (query.data?.totalPages ?? 1)) {
      setCurrentPage(page);
    }
  };

  const setPage = (page: number) => {
    setCurrentPage(page);
  };

  return {
    currentPage,
    canGoPrevious,
    canGoNext,
    goToPrevious,
    goToNext,
    goToPage,
    setPage,
    totalPages: query.data?.totalPages ?? 1,
    totalCount: query.data?.totalCount ?? 0,
    jobs: query.data?.jobs ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
  };
}

// Static method to refetch queries for a specific type
useScrapingJobs.refetch = async (type: ScrapingJobType) => {
  const queryKeyPrefix = generateQueryKeyPrefix(type);
  await queryClient.refetchQueries({ queryKey: queryKeyPrefix });
};
